import React, { useCallback, useRef, useEffect } from 'react';
import { useAudioPlayer } from '../hooks/useAudioPlayer';
import analytics from '../services/analytics';

interface TrailerPlayerProps {
  bookId: string;
  audioFile?: string;
  bookTitle: string;
  chapterTitle?: string;
  onAudioEnded?: () => void;
  onAudioStateChange?: (state: {isPlaying: boolean, hasStopped: boolean, hasStartedPlaying: boolean}) => void;
}

const TrailerPlayer: React.FC<TrailerPlayerProps> = ({ bookId, audioFile, bookTitle, chapterTitle, onAudioEnded, onAudioStateChange }) => {
  // Use audio file directly (it's already a full URL from API or relative path for static files)
  const audioPath = audioFile || null;
  
  // Determine context: if onAudioEnded is provided, we're in chapter context
  const isChapterContext = Boolean(onAudioEnded);
  
  // Track audio engagement
  const playStartTimeRef = useRef<number | null>(null);
  const hasTrackedPlayRef = useRef(false);
  const hasTrackedCompletionRef = useRef(false);
  
  // Debug logging (reduced)
  // console.log('TrailerPlayer props:', { bookId, audioFile, bookTitle, chapterTitle });
  
  // Stable error handler
  const handleError = useCallback((errorMessage: string) => {
    analytics.trackAudioLoadingFailed(bookId, audioFile || '', errorMessage);
  }, [bookId, audioFile]);
  
  // Use the custom audio player hook first
  const {
    isPlaying,
    isLoading,
    currentTime,
    duration,
    error,
    hasStopped,
    hasStartedPlaying,
    play: originalPlay,
    stop: originalStop,
    seek,
  } = useAudioPlayer(audioPath, {
    autoPlay: isChapterContext, // Auto-play for book chapters
    onEnded: () => {
      // Track completion when audio ends
      if (playStartTimeRef.current !== null && !hasTrackedCompletionRef.current) {
        if (isChapterContext) {
          // This is chapter audio
          analytics.trackAudioCompleted(bookId, audioFile || '', duration);
        } else {
          // This is a trailer
          analytics.trackTrailerCompleted(bookId, duration, duration);
        }
        
        // Reset tracking state
        playStartTimeRef.current = null;
        hasTrackedPlayRef.current = false;
        hasTrackedCompletionRef.current = true;
      }
      
      // Call original handler
      if (onAudioEnded) {
        onAudioEnded();
      }
    },
    onError: handleError,
  });

  // Notify parent component of audio state changes
  useEffect(() => {
    if (onAudioStateChange) {
      onAudioStateChange({
        isPlaying,
        hasStopped,
        hasStartedPlaying
      });
    }
  }, [isPlaying, hasStopped, hasStartedPlaying, onAudioStateChange]);


  // Enhanced play handler with analytics
  const handlePlay = useCallback(async () => {
    if (!hasTrackedPlayRef.current && !isPlaying) {
      // Track play start
      if (isChapterContext) {
        // This is chapter audio
        analytics.trackAudioPlayStarted(bookId, audioFile || '', true);
      } else {
        // This is a trailer
        analytics.trackTrailerPlayStarted(bookId, bookTitle, 'homepage');
      }
      
      playStartTimeRef.current = currentTime;
      hasTrackedPlayRef.current = true;
      hasTrackedCompletionRef.current = false; // Reset completion tracking
    }
    
    await originalPlay();
  }, [bookId, audioFile, bookTitle, isChapterContext, currentTime, isPlaying, originalPlay]);

  // Enhanced stop handler with analytics
  const handleStop = useCallback(() => {
    if (playStartTimeRef.current !== null && !isChapterContext) {
      // Track early stop for trailers
      analytics.trackTrailerStoppedEarly(bookId, currentTime, duration);
    }
    
    // Reset tracking state
    playStartTimeRef.current = null;
    hasTrackedPlayRef.current = false;
    
    originalStop();
  }, [bookId, isChapterContext, currentTime, duration, originalStop]);


  const formatTime = (timeInSeconds: number) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleProgressBarClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (duration === 0) return;

    const progressBar = e.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const progressBarWidth = rect.width;
    
    // Calculate the clicked position as a percentage (0-1)
    const clickedPercentage = Math.max(0, Math.min(1, clickX / progressBarWidth));
    
    // Convert to time and seek
    const targetTime = clickedPercentage * duration;
    seek(targetTime);
  };

  // Don't render if no audio file is available
  if (!audioPath) {
    return null;
  }


  return (
    <div className="trailer-player">
      <div className="trailer-controls">
        <button
          className={`play-button ${isPlaying ? 'playing' : ''}`}
          onClick={handlePlay}
          disabled={isLoading}
          title={
            isChapterContext
              ? isPlaying
                ? `Pause ${chapterTitle ? `${bookTitle}: ${chapterTitle}` : `${bookTitle} Chapter`}`
                : `Play ${chapterTitle ? `${bookTitle}: ${chapterTitle}` : `${bookTitle} Chapter`}`
              : isPlaying
                ? `Pause ${bookTitle} Trailer`
                : `Play ${bookTitle} Trailer`
          }
        >
          {isLoading ? (
            <span className="loading-spinner">⟳</span>
          ) : isPlaying ? (
            <span className="pause-icon">⏸</span>
          ) : (
            <span className="play-icon">▶</span>
          )}
        </button>

        {isPlaying && (
          <button
            className="stop-button"
            onClick={handleStop}
            title={`Stop ${bookTitle}`}
          >
            <span className="stop-icon">⏹</span>
          </button>
        )}

        <div className="trailer-info">
          <span className="trailer-label">
            {isChapterContext ? (
              // Chapter context - show book and chapter
              isPlaying ? 
                `Playing ${chapterTitle ? `${bookTitle}: ${chapterTitle}` : `${bookTitle} Chapter`}...` :
                hasStopped ? 
                  `Stopped` :
                  hasStartedPlaying && !isPlaying ?
                    `Paused` :
                    `Play ${chapterTitle ? `${bookTitle}: ${chapterTitle}` : `${bookTitle} Chapter`}`
            ) : (
              // Trailer context - show trailer text
              isPlaying ? 'Playing trailer...' : 
              hasStopped ? 'Stopped' :
              hasStartedPlaying && !isPlaying ? 'Paused' : 'Play Trailer'
            )}
          </span>
          
          {(isPlaying || currentTime > 0) && (
            <span className="time-display">
              {formatTime(currentTime)}
              {duration > 0 && ` / ${formatTime(duration)}`}
            </span>
          )}
        </div>
      </div>

      {error && (
        <div className="trailer-error">
          <span className="error-message">{error}</span>
        </div>
      )}

      {duration > 0 && (chapterTitle || hasStartedPlaying) && !hasStopped && (
        <div 
          className="progress-bar" 
          onClick={handleProgressBarClick}
          style={{ cursor: 'pointer' }}
          title="Click to seek"
        >
          <div 
            className="progress-fill"
            style={{ width: `${(currentTime / duration) * 100}%` }}
          />
        </div>
      )}
    </div>
  );
};

export default TrailerPlayer;